import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import { useDatabaseContent } from '@databases/database-state';
import { DragType } from '@databases/drag';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { isModifierKeyPressed, useFocusInputField } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectFirstSelectedValue } from '@databases/redux/cellSelections/selectors';
import { doClearValues } from '@databases/redux/contextual-menu/actions';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import { type Participant } from '@databases/utils/presence/types';
import { sleep } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import React, {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

export const InlineEditContainer = styled.form.attrs({ action: '#' })<{
  isEditing?: boolean;
  isManual?: boolean;
  preserveEditWidth?: boolean;
  hasThreads?: boolean;
  participants: Participant[];
  isReadOnly: boolean;
  isSelected?: boolean;
  collaboratorColor?: string;
}>`
  ${({ isSelected, isReadOnly, collaboratorColor }) => {
    const borderColor =
      isSelected || isReadOnly
        ? undefined
        : // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
          collaboratorColor && token(collaboratorColor);

    return (
      borderColor &&
      css`
        border: ${token('border.width.selected', '2px')} solid ${borderColor};
        border-radius: ${token('radius.small', '3px')};
      `
    );
  }}

  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  padding-right: ${(props) => (props.hasThreads ? '36px' : '0')};

  ${(props) =>
    props.isEditing || props.isManual
      ? ''
      : css`
          transition: background 0.2s;

          &:hover {
            background: ${token('elevation.surface.hovered', '#F1F2F4')};
          }
        `}
  > div {
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    > * {
      flex-grow: 1;
    }
  }

  > :nth-child(${(props) => (props.isEditing ? 1 : 2)}) {
    ${({ preserveEditWidth }) =>
      preserveEditWidth
        ? css`
            visibility: hidden; // not using "display: none" to preserve the width of the element for both modes
          `
        : css`
            display: none;
          `}
  }
`;

export interface InlineEditStatelessProps<V> {
  readView: () => ReactNode;
  editView: () => ReactNode;
  isEditing: boolean;
  isReadOnly: boolean;
  preserveEditWidth?: boolean;
  onIsEditingChange: (isEditing: boolean) => void;
  /** If true, the edit mode cannot be activated (but deactivated) by the user, but only programmatically by changing the isEditing prop. */
  isManual?: boolean;

  /**
   * The current value of the field. When the user resets the field (by pressing escape), the latest value when the field was switched to
   * edit mode is passed to the onReset prop.
   */
  value: V;
  onReset: (value: V) => void;
  onSubmit?: (value: V) => void;
  onBlur?: (value: V) => void;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  enableSelection?: boolean;
  tooltipContent?: ReactNode;
  isSelected?: boolean;
  field?: DatabaseField<DatabaseFieldType>;
  entryId?: string;
  hasThreads?: boolean;
  shouldShowHoverButton?: boolean;
}

const MissingConfigurationTooltip = ({
  children,
  tooltipContent,
}: {
  children: ReactNode;
  tooltipContent?: ReactNode;
}) =>
  tooltipContent ? (
    <Tooltip content={tooltipContent}>{children}</Tooltip>
  ) : (
    <>{children}</>
  );

export function InlineEditStateless<V>({
  readView,
  editView,
  isEditing,
  preserveEditWidth,
  onIsEditingChange,
  isManual,
  value,
  onReset,
  onSubmit,
  onBlur,
  onIsSelectedChange,
  enableSelection,
  tooltipContent,
  isSelected,
  field,
  entryId,
  hasThreads = false,
  shouldShowHoverButton,
  isReadOnly,
}: InlineEditStatelessProps<V>): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusInputField(
    isEditing && containerRef.current ? containerRef.current : undefined
  );

  const { closeMenu, isMenuOpen } = useContextualMenu();
  const { view } = useView();
  const { contentId } = useDatabaseContent();
  const { openThread } = useCommentStateContext();

  const store = defaultStoreUtils.useStore();

  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  /**
   * Selects the cell initially when the user starts to drag the drag caret to fill values or adds the cell to the selection on hover and
   * drag. Also set dragging state to show the drag to fill indicator.
   **/
  useEffect(() => {
    /*
     * Select text inside a text area which is a child of a draggable
     * element doesn't work in Firefox: https://bugzilla.mozilla.org/show_bug.cgi?id=800050
     * Workaround is to set draggable to "false" for the text area when it is being edited
     * */
    if (
      isSelectionsPerformanceEnabled ||
      !containerRef.current ||
      isEditing ||
      !enableSelection
    ) {
      return;
    }

    return combine(
      draggable({
        element: containerRef.current,
        getInitialData: () => ({
          type: DragType.SELECT_VALUES,
        }),
        onDrop: () => {
          preventUnhandled.stop();
        },
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          // disable the native drag preview
          disableNativeDragPreview({ nativeSetDragImage });
          // we don't want any native drop animation for when the user
          // does not drop on a drop target. we want the drag to finish immediately
          preventUnhandled.start();
        },
      }),
      dropTargetForElements({
        element: containerRef.current,
        onDragStart: ({ source }) => {
          if (source.data.type === DragType.SELECT_VALUES) {
            onIsSelectedChange?.();
          }
        },
        onDragEnter: ({ source }) => {
          if (source.data.type === DragType.SELECT_VALUES && !isSelected) {
            // Fixes issue where first cell has white background when performing multiselect
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }

            removeTextSelection();
            onIsSelectedChange?.({ isMultiSelectOn: true });
          }
        },
      })
    );
  }, [
    containerRef,
    enableSelection,
    isEditing,
    isSelected,
    onIsSelectedChange,
    isSelectionsPerformanceEnabled,
  ]);

  const isShiftKeyPressed = useRef(false);
  const [isUsingIME, setIsUsingIME] = useState(false);
  const justEnteredEditModeFromSpan = useRef(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const commentThreadsPopup = document.getElementById('comment-threads-popup');

      if (commentThreadsPopup && commentThreadsPopup.contains(target)) {
        return;
      }

      const isMultiSelectOn =
        isShiftKeyPressed.current || isModifierKeyPressed(e, ['shiftKey']);
      if (enableSelection && isMultiSelectOn) {
        e.preventDefault();
        onIsSelectedChange?.({ isMultiSelectOn });
      } else if (
        !isMultiSelectOn &&
        (!isEditing || field?.type === DatabaseFieldType.ATTACHMENT) &&
        !isManual &&
        !(
          e.target instanceof Element &&
          e.target.closest('a[href],input,button,select,textarea,area[href]')
        )
      ) {
        if (
          field?.type === DatabaseFieldType.ATTACHMENT &&
          e.target instanceof Element &&
          (e.target.closest('.media-file-card-view') ||
            e.target.closest('.media-card-inline-player') ||
            e.target.tagName === 'IMG' ||
            e.target.tagName === 'VIDEO')
        ) {
          return;
        }

        // Wait for selection to be updated and only switch to editing mode if nothing is selected. This prevents switching
        // to edit mode when the click event was triggered by a user selecting text.
        await sleep(0);
        const selection = getSelection();
        const hasTextSelection = selection?.type === 'Range';
        const isSelectionWithinContainer =
          selection?.anchorNode && containerRef.current?.contains(selection.anchorNode);
        const isDoubleClick = e.detail === 2;
        // Allows edit mode if there is no text selection, selection is outside the container, or user double-clicked
        // (double-click selects a word by default, but the user intent is to edit)
        if (!hasTextSelection || !isSelectionWithinContainer || isDoubleClick) {
          if (
            isSelected ||
            (e.target instanceof Element && e.target.closest('th')) || // don't double click to edit field names
            (e.target instanceof Element && e.target.closest('h1')) // don't double click to edit database title
          ) {
            onIsEditingChange(true);
          }
          closeMenu();
          if (enableSelection) {
            onIsSelectedChange?.();
          }
        }
      }
    },
    [
      enableSelection,
      isEditing,
      field?.type,
      isManual,
      onIsSelectedChange,
      isSelected,
      closeMenu,
      onIsEditingChange,
    ]
  );

  const handleFocus = useCallback(() => {
    if (!isEditing) {
      justEnteredEditModeFromSpan.current = true;
      onIsEditingChange(true);
      closeMenu();

      if (enableSelection) {
        onIsSelectedChange?.();
      }
    }
  }, [closeMenu, enableSelection, isEditing, onIsEditingChange, onIsSelectedChange]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      isShiftKeyPressed.current = false;
      if (!e.currentTarget.contains(e.relatedTarget)) {
        // Guard against the span's own unmount blur.
        if (justEnteredEditModeFromSpan.current) {
          justEnteredEditModeFromSpan.current = false;
          return;
        }

        // Suppress spurious blur when the read-view Box loses focus as the edit input renders.
        if ((e.target as HTMLElement).hasAttribute('data-inline-edit-read-view')) {
          return;
        }

        // When an editor popup (e.g. typeahead, link toolbar, emoji picker) is
        // dismissed, its DOM elements are removed, causing a blur with
        // relatedTarget=null. If a ProseMirror editor is active in the container,
        // focus is likely returning to the editor after the popup closes.
        // We use requestAnimationFrame to verify focus actually left the container
        // before committing the blur.
        if (e.relatedTarget === null && e.currentTarget instanceof HTMLElement) {
          const container = e.currentTarget;
          const hasTypeahead = container.querySelector('.fabric-editor-typeahead');
          if (hasTypeahead) {
            e.stopPropagation();
            return;
          }

          // autoFocus moves activeElement synchronously — if focus is already in
          // the container this is not a genuine blur.
          const hasPopoverEdit = container.querySelector(
            '[data-testid="popover-edit-container"]'
          );

          if (hasPopoverEdit) {
            const blurredElement = e.target as HTMLElement;
            requestAnimationFrame(() => {
              // If focus moved to a different element inside the container, the
              // user deliberately interacted with something (e.g. a dropdown
              // option rendered inside the container) — keep the editor open.
              if (
                container.contains(document.activeElement) &&
                document.activeElement !== blurredElement
              ) {
                return;
              }
              // Focus either left the container entirely, or returned to the
              // same input that just blurred (programmatic refocus) — close.
              onIsEditingChange(false);
              onBlur?.(value);
            });
            return;
          }

          if (container.contains(document.activeElement)) {
            return;
          }

          // ProseMirror popups dismiss and return focus asynchronously — defer.
          const hasProseMirror = container.querySelector('.ProseMirror');
          if (hasProseMirror) {
            requestAnimationFrame(() => {
              if (container.contains(document.activeElement)) {
                return;
              }
              onIsEditingChange(false);
              onBlur?.(value);
            });
            return;
          }
        }

        e.stopPropagation();
        onIsEditingChange(false);
        onBlur?.(value);
      }
    },
    [onBlur, onIsEditingChange, value]
  );

  const handleCompositionStart = useCallback(() => {
    setIsUsingIME(true);
  }, []);

  const handleCompositionEnd = useCallback(() => {
    setIsUsingIME(false);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      if (!isUsingIME) {
        blurActiveElement(); // Removing this may prevent database name from being updated
      }
      e.preventDefault();
      onSubmit?.(value);
    },
    [isUsingIME, onSubmit, value]
  );

  const previousValue = useRef(value);
  useEffect(() => {
    if (isEditing) {
      // Backup value when switching to edit mode so that we can revert to this value.
      previousValue.current = value;
    }
  }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onReset(previousValue.current);
        onIsEditingChange(false);
        blurActiveElement();
      }
      if (e.key === 'Shift') {
        isShiftKeyPressed.current = true;
      }
    },
    [onIsEditingChange, onReset]
  );

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Shift') {
      isShiftKeyPressed.current = false;
    }
  }, []);

  const commentThreadDialogOpen = useMemo(
    () => openThread?.valueId !== undefined,
    [openThread.valueId]
  );

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isPasteOperation = (e.ctrlKey || e.metaKey) && e.key === 'v';
      const isIgnoredKey =
        e.key === 'Backspace' ||
        e.key === 'Escape' ||
        e.key === 'Shift' ||
        e.ctrlKey ||
        e.metaKey ||
        isPasteOperation;

      if (
        !contentId ||
        !isSelected ||
        isIgnoredKey ||
        isMenuOpen ||
        commentThreadDialogOpen
      ) {
        return;
      }

      const target = e.target as Element | null;
      const isTargetInTextInput = target?.closest(
        'input, textarea, [contenteditable], .ProseMirror, [role="textbox"]'
      );
      if (isTargetInTextInput) {
        return;
      }

      const firstSelectedValue = selectFirstSelectedValue(store.getState());
      const isCurrentFieldFirstSelected =
        (firstSelectedValue?.entryId === entryId &&
          firstSelectedValue?.fieldId === field?.id) ||
        view.settings.layout.type !== DatabaseLayoutType.TABLE;

      if (isCurrentFieldFirstSelected) {
        onIsEditingChange(true);
        onIsSelectedChange?.();
      }
    };

    if (isSelected) {
      window.addEventListener('keydown', handleKeyPress);
    } else {
      window.removeEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    commentThreadDialogOpen,
    contentId,
    entryId,
    field?.id,
    isMenuOpen,
    isSelected,
    onIsEditingChange,
    onIsSelectedChange,
    openThread.valueId,
    store,
    view.settings.layout.type,
  ]);

  const shouldShowTelepointers = !isSelected && !isReadOnly && field?.id && entryId;

  const { participants, recentCollaboratorColor } = usePresence({
    grouping: field?.id ? { fieldId: field.id, entryId } : undefined,
    componentId: 'InlineEdit',
    isEnabled: Boolean(shouldShowTelepointers),
  });

  return (
    <InlineEditContainer
      preserveEditWidth={preserveEditWidth}
      isEditing={isEditing}
      isManual={isManual}
      /**
       * This conditionally adds an editor-open data attribute to this component when editing is
       * true, so we can measure how long it takes to open this.
       *
       * @see databases/packages/frontend-metrics/src/metrics/cell-inline-edit.ts
       */
      {...(isEditing ? { 'data-database-cell-editor-open': '' } : {})}
      onBlur={handleBlur}
      onClick={handleClick}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      ref={containerRef}
      hasThreads={hasThreads}
      collaboratorColor={recentCollaboratorColor}
      isReadOnly={isReadOnly}
      isSelected={isSelected}
    >
      {shouldShowTelepointers && containerRef && shouldShowHoverButton && (
        <PresenceNameplate participants={participants} anchorRef={containerRef} />
      )}
      <MissingConfigurationTooltip tooltipContent={tooltipContent}>
        {contentId && (
          <ContentView
            contentId={contentId}
            isSelected={Boolean(isSelected)}
            isEditing={isEditing}
            isReadOnly={isReadOnly}
          >
            {readView()}
          </ContentView>
        )}
      </MissingConfigurationTooltip>
      {isEditing && editView()}
      <input type="submit" style={{ display: 'none' }} />
      {!isEditing && !isManual && (
        // FIXME: Accessibility issue - jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex
        <span tabIndex={0} onFocus={handleFocus} data-inline-edit-focus-span />
      )}
    </InlineEditContainer>
  );
}

const ContentView = ({
  children,
  contentId,
  isSelected,
  isEditing,
  isReadOnly,
}: {
  children: ReactNode;
  contentId: string;
  isSelected: boolean;
  isEditing: boolean;
  isReadOnly: boolean;
}) => {
  const dispatch = defaultStoreUtils.useDispatch();

  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  const handleKeyDown = useMemo(
    () =>
      isSelectionsPerformanceEnabled
        ? undefined
        : (e: React.KeyboardEvent) => {
            if (!isReadOnly && !isEditing && e.key === 'Backspace' && isSelected) {
              const target = e.target as HTMLElement;
              const commentThreadsPopup = document.getElementById(
                'comment-threads-popup'
              );

              if (commentThreadsPopup && commentThreadsPopup.contains(target)) {
                return;
              }

              e.stopPropagation();
              dispatch(doClearValues(contentId));
            }
          },
    [
      isSelectionsPerformanceEnabled,
      contentId,
      dispatch,
      isEditing,
      isSelected,
      isReadOnly,
    ]
  );

  return (
    <Box
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-inline-edit-read-view
      xcss={xcss({
        // TODO: Remove this once we have proper focus and focus ring in our app.
        ':focus': {
          outline: 'none',
        },
      })}
    >
      {children}
    </Box>
  );
};
